import React from 'react';
import { Lightbulb, Scale, AlertTriangle, Briefcase, CalendarClock, ListOrdered, Table2 } from 'lucide-react';

const CALLOUTS = {
  did_you_know: { label: 'Did you know?', icon: Lightbulb, cls: 'border-sky-500/40 bg-sky-500/10', accent: 'text-sky-300' },
  regulatory: { label: 'Regulatory note', icon: Scale, cls: 'border-violet-500/40 bg-violet-500/10', accent: 'text-violet-300' },
  pitfall: { label: 'Common pitfall', icon: AlertTriangle, cls: 'border-rose-500/40 bg-rose-500/10', accent: 'text-rose-300' },
  case: { label: 'Real-world case', icon: Briefcase, cls: 'border-emerald-500/40 bg-emerald-500/10', accent: 'text-emerald-300' },
};

const BlockTitle = ({ icon: Icon, children }) => children ? (
  <h4 className="flex items-center gap-2 text-white font-semibold mb-4"><Icon className="h-4 w-4 text-navigate-gold" />{children}</h4>
) : null;

const TableBlock = ({ b }) => (
  <div>
    <BlockTitle icon={Table2}>{b.title}</BlockTitle>
    <div className="overflow-x-auto rounded-xl border border-zinc-700">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="bg-navigate-gold/15">
            {b.headers.map((h, i) => <th key={i} className="text-left px-4 py-3 font-semibold text-navigate-gold border-b border-zinc-700">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {b.rows.map((r, i) => (
            <tr key={i} className={i % 2 ? 'bg-zinc-900/40' : 'bg-zinc-900/80'}>
              {r.map((c, j) => <td key={j} className={`px-4 py-3 align-top border-b border-zinc-800 ${j === 0 ? 'text-white font-medium' : 'text-zinc-300'}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TimelineBlock = ({ b }) => (
  <div>
    <BlockTitle icon={CalendarClock}>{b.title}</BlockTitle>
    <ol className="relative border-l-2 border-navigate-gold/40 ml-3 space-y-5">
      {b.events.map((e, i) => (
        <li key={i} className="pl-6 relative">
          <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-navigate-gold ring-4 ring-zinc-900" />
          <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-navigate-gold/15 text-navigate-gold mb-1">{e.date}</span>
          <p className="text-white font-medium">{e.title}</p>
          {e.description && <p className="text-sm text-zinc-400 mt-0.5">{e.description}</p>}
        </li>
      ))}
    </ol>
  </div>
);

const ProcessBlock = ({ b }) => (
  <div>
    <BlockTitle icon={ListOrdered}>{b.title}</BlockTitle>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {b.steps.map((s, i) => (
        <div key={i} className="relative rounded-xl border border-zinc-700 bg-zinc-900/70 p-4">
          <span className="h-8 w-8 rounded-full bg-teal-500/20 text-teal-300 font-bold flex items-center justify-center mb-2">{i + 1}</span>
          <p className="text-white font-medium">{s.title}</p>
          {s.description && <p className="text-sm text-zinc-400 mt-1">{s.description}</p>}
        </div>
      ))}
    </div>
  </div>
);

const CalloutBlock = ({ b }) => {
  const v = CALLOUTS[b.variant] || CALLOUTS.did_you_know;
  const Icon = v.icon;
  return (
    <div className={`rounded-xl border-l-4 border ${v.cls} p-4 flex gap-3`}>
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${v.accent}`} />
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wider ${v.accent}`}>{v.label}</p>
        {b.title && <p className="text-white font-medium mt-1">{b.title}</p>}
        <p className="text-sm text-zinc-300 mt-1 leading-relaxed">{b.text}</p>
      </div>
    </div>
  );
};

const RENDER = { table: TableBlock, timeline: TimelineBlock, process: ProcessBlock, callout: CalloutBlock };

// Renders NAVI visual blocks (tables, timelines, process flows, callouts) for a course section.
export default function VisualBlocks({ blocks, testIdPrefix = 'visual-block' }) {
  if (!blocks?.length) return null;
  return (
    <div className="space-y-6 my-6">
      {blocks.map((b, i) => {
        const C = RENDER[b.type];
        return C ? <div key={i} data-testid={`${testIdPrefix}-${b.type}-${i}`}><C b={b} /></div> : null;
      })}
    </div>
  );
}
